import { useState, useEffect, useCallback, type ReactNode } from 'react';
import type { Position } from '../types';
import { useMarketDataContext } from './MarketDataContext.utils';
import {
  EditorModeContext,
  IDLE_MODE,
  getSelectedUids,
  modeFromUids,
  type EditorMode,
} from './EditorModeContext.utils';
import {
  alignToLine,
  computeBestFitDirection,
  distributeAlongLine,
  type Direction,
} from '../utils/alignment';

function quoteCsv(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

const UNDO_LIMIT = 20;

// `?edit=true` on the first page load unlocks the prod editor. Captured
// once at module init (the URL doesn't change meaningfully for this flag
// during an app session), so the E-key toggle stays enabled for the rest
// of the session even after Esc-ing out.
const EDIT_URL_UNLOCK = typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('edit') === 'true';

export function EditorModeProvider({ children }: { children: ReactNode }) {
  const {
    state: marketData,
    movePosition: movePositionInData,
    movePositionsBulk: movePositionsBulkInData,
    addPosition: addPositionInData,
    updatePosition: updatePositionInData,
    renamePosition: renamePositionInData,
    deletePosition: deletePositionInData,
  } = useMarketDataContext();

  const [isEditing, setIsEditing] = useState(EDIT_URL_UNLOCK);
  const [mode, setMode] = useState<EditorMode>(IDLE_MODE);
  const [dirtyPositionUids, setDirtyPositionUids] = useState<Set<string>>(new Set());
  const [deletedPositionIds, setDeletedPositionIds] = useState<Set<string>>(new Set());
  // Stack of pre-move coordinate snapshots. Pushed by every coord-mutating
  // call (single move, bulk align, bulk distribute); popped by undo().
  // Capped at UNDO_LIMIT so long editing sessions don't leak state.
  const [undoStack, setUndoStack] = useState<Array<Map<string, [number, number]>>>([]);

  const markDirty = useCallback((uid: string) => {
    setDirtyPositionUids(prev => {
      if (prev.has(uid)) return prev;
      const next = new Set(prev);
      next.add(uid);
      return next;
    });
  }, []);

  const markDirtyMany = useCallback((uids: Iterable<string>) => {
    setDirtyPositionUids(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const uid of uids) {
        if (!next.has(uid)) {
          next.add(uid);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  const selectPosition = useCallback((uid: string) => {
    setMode({ kind: 'single-select', uid });
  }, []);

  const toggleInSelection = useCallback((uid: string) => {
    setMode(prev => {
      const current = getSelectedUids(prev);
      const next = new Set(current);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return modeFromUids(next);
    });
  }, []);

  const addToSelection = useCallback((uids: Iterable<string>) => {
    setMode(prev => {
      const current = getSelectedUids(prev);
      const next = new Set(current);
      let changed = false;
      for (const uid of uids) {
        if (!next.has(uid)) {
          next.add(uid);
          changed = true;
        }
      }
      return changed ? modeFromUids(next) : prev;
    });
  }, []);

  const clearSelection = useCallback(() => setMode(IDLE_MODE), []);

  const pushUndo = useCallback((snapshot: Map<string, [number, number]>) => {
    if (snapshot.size === 0) return;
    setUndoStack(prev => {
      const next = prev.length >= UNDO_LIMIT ? prev.slice(1) : [...prev];
      next.push(snapshot);
      return next;
    });
  }, []);

  const snapshotCoords = useCallback((uids: Iterable<string>): Map<string, [number, number]> => {
    const snapshot = new Map<string, [number, number]>();
    for (const uid of uids) {
      const p = marketData.positions.find(x => x.uid === uid);
      if (p) snapshot.set(uid, [p.coordinates[0], p.coordinates[1]]);
    }
    return snapshot;
  }, [marketData.positions]);

  const beginPlacement = useCallback(() => {
    setMode({ kind: 'placement-awaiting-click' });
  }, []);

  const setPlacementCoords = useCallback((coords: [number, number]) => {
    setMode({ kind: 'placement-awaiting-details', coords });
  }, []);

  const cancelPlacement = useCallback(() => setMode(IDLE_MODE), []);

  const movePosition = useCallback((uid: string, coordinates: [number, number]) => {
    pushUndo(snapshotCoords([uid]));
    movePositionInData(uid, coordinates);
    markDirty(uid);
  }, [movePositionInData, markDirty, pushUndo, snapshotCoords]);

  const movePositionsGroup = useCallback((anchorUid: string, newAnchorCoords: [number, number]) => {
    const selectedUids = getSelectedUids(mode);
    if (!selectedUids.has(anchorUid) || selectedUids.size <= 1) {
      movePosition(anchorUid, newAnchorCoords);
      return;
    }
    const anchor = marketData.positions.find(p => p.uid === anchorUid);
    if (!anchor) return;
    const deltaLat = newAnchorCoords[0] - anchor.coordinates[0];
    const deltaLng = newAnchorCoords[1] - anchor.coordinates[1];
    pushUndo(snapshotCoords(selectedUids));
    const updates = new Map<string, [number, number]>();
    for (const uid of selectedUids) {
      const pos = marketData.positions.find(p => p.uid === uid);
      if (!pos) continue;
      updates.set(uid, [pos.coordinates[0] + deltaLat, pos.coordinates[1] + deltaLng]);
    }
    movePositionsBulkInData(updates);
    markDirtyMany(updates.keys());
  }, [mode, marketData.positions, movePositionsBulkInData, markDirtyMany, pushUndo, snapshotCoords, movePosition]);

  const commitPlacement = useCallback((fields: Omit<Position, 'coordinates' | 'uid'>) => {
    if (mode.kind !== 'placement-awaiting-details') return;
    const newPosition: Position = { ...fields, uid: crypto.randomUUID(), coordinates: mode.coords };
    addPositionInData(newPosition);
    markDirty(newPosition.uid);
    setMode(IDLE_MODE);
  }, [mode, addPositionInData, markDirty]);

  const updatePosition = useCallback((uid: string, patch: Partial<Pick<Position, 'type' | 'labelPosition' | 'name' | 'description'>>) => {
    updatePositionInData(uid, patch);
    markDirty(uid);
  }, [updatePositionInData, markDirty]);

  const renamePosition = useCallback((uid: string, newId: string) => {
    const pos = marketData.positions.find(p => p.uid === uid);
    if (pos && pos.positionId === newId) return;
    renamePositionInData(uid, newId);
    markDirty(uid);
  }, [marketData.positions, renamePositionInData, markDirty]);

  const deletePosition = useCallback((uid: string) => {
    const pos = marketData.positions.find(p => p.uid === uid);
    const remainingWithSamePid = pos
      ? marketData.positions.some(p => p.positionId === pos.positionId && p.uid !== uid)
      : true;
    deletePositionInData(uid);
    setDirtyPositionUids(prev => {
      if (!prev.has(uid)) return prev;
      const next = new Set(prev);
      next.delete(uid);
      return next;
    });
    if (pos && !remainingWithSamePid) {
      setDeletedPositionIds(prev => {
        if (prev.has(pos.positionId)) return prev;
        const next = new Set(prev);
        next.add(pos.positionId);
        return next;
      });
    }
    setMode(prev => {
      if (prev.kind === 'single-select' && prev.uid === uid) return IDLE_MODE;
      if (prev.kind === 'multi-select' && prev.uids.has(uid)) {
        const next = new Set(prev.uids);
        next.delete(uid);
        return modeFromUids(next);
      }
      return prev;
    });
  }, [deletePositionInData, marketData.positions]);

  const applyBulkAlignment = useCallback((
    op: (positions: Position[], direction: Direction) => Map<string, [number, number]>,
  ) => {
    const selectedUids = getSelectedUids(mode);
    if (selectedUids.size < 2) return;
    const selected = marketData.positions.filter(p => selectedUids.has(p.uid));
    if (selected.length < 2) return;
    const direction = computeBestFitDirection(selected);
    const updates = op(selected, direction);
    if (updates.size === 0) return;
    pushUndo(snapshotCoords(updates.keys()));
    movePositionsBulkInData(updates);
    markDirtyMany(updates.keys());
  }, [mode, marketData.positions, movePositionsBulkInData, markDirtyMany, pushUndo, snapshotCoords]);

  const alignSelection = useCallback(() => {
    applyBulkAlignment(alignToLine);
  }, [applyBulkAlignment]);

  const distributeSelection = useCallback(() => {
    applyBulkAlignment(distributeAlongLine);
  }, [applyBulkAlignment]);

  const undo = useCallback(() => {
    setUndoStack(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1]!;
      // Skip snapshot entries for positions that have since been deleted —
      // otherwise movePositionsBulk silently no-ops on them AND we'd
      // re-flag their uids dirty, polluting the CSV warning list.
      const liveUids = new Set(marketData.positions.map(p => p.uid));
      const filtered = new Map<string, [number, number]>();
      for (const [uid, coords] of last) {
        if (liveUids.has(uid)) filtered.set(uid, coords);
      }
      if (filtered.size > 0) {
        movePositionsBulkInData(filtered);
        markDirtyMany(filtered.keys());
      }
      return prev.slice(0, -1);
    });
  }, [movePositionsBulkInData, markDirtyMany, marketData.positions]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const target = document.activeElement;
      const inField = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA');
      const canToggle = import.meta.env.DEV || EDIT_URL_UNLOCK;
      if (canToggle && !inField && (e.key === 'e' || e.key === 'E')) {
        setIsEditing(prev => !prev);
        return;
      }
      if (!inField && isEditing && (e.key === 'a' || e.key === 'A') && !(e.metaKey || e.ctrlKey)) {
        if (mode.kind === 'idle') {
          setMode({ kind: 'placement-awaiting-click' });
        }
        return;
      }
      if (!inField && isEditing && (e.key === 'z' || e.key === 'Z') && (e.metaKey || e.ctrlKey) && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      if (e.key === 'Escape') {
        if (mode.kind !== 'idle') {
          setMode(IDLE_MODE);
        } else {
          setIsEditing(false);
        }
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isEditing, mode, undo]);

  useEffect(() => {
    const hasChanges = dirtyPositionUids.size > 0 || deletedPositionIds.size > 0;
    if (!hasChanges) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirtyPositionUids, deletedPositionIds]);

  const positions = marketData.positions;

  const exportCSV = useCallback((): string => {
    const header = 'position_id,coordinates,type,label_position,name,description';
    const rows = positions
      .map(p => {
        const coords = `${p.coordinates[0]} ${p.coordinates[1]}`;
        return [
          p.positionId,
          quoteCsv(coords),
          p.type,
          p.labelPosition,
          p.name ? quoteCsv(p.name) : '',
          p.description ? quoteCsv(p.description) : '',
        ].join(',');
      });
    const lines = [header, ...rows];
    if (deletedPositionIds.size > 0) {
      lines.push('');
      lines.push('# Deleted (remove these rows from Sheets manually):');
      lines.push(`# ${[...deletedPositionIds].join(', ')}`);
    }
    return lines.join('\n');
  }, [positions, deletedPositionIds]);

  return (
    <EditorModeContext.Provider value={{
      isEditing,
      mode,
      positions,
      dirtyPositionUids,
      deletedPositionIds,
      selectPosition,
      toggleInSelection,
      addToSelection,
      clearSelection,
      beginPlacement,
      setPlacementCoords,
      cancelPlacement,
      commitPlacement,
      movePosition,
      movePositionsGroup,
      updatePosition,
      renamePosition,
      deletePosition,
      alignSelection,
      distributeSelection,
      canUndo: undoStack.length > 0,
      undo,
      exportCSV,
    }}>
      {children}
    </EditorModeContext.Provider>
  );
}
